import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Tooltip,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Checkbox,
} from "@mui/material";
import * as Mui from "@mui/material";
import { showToast } from "services/utils/Status";
import { Edit, EventNote, HelpOutline, AccessTime } from "@mui/icons-material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm, Controller } from "react-hook-form";
import {
  PrimaryButton,
  SecondaryButton,
} from "components/CustomComponents/DesignSystemButtons";
import { GlobalConfirmDialog } from "components/DesignSystem/sharedUiPatterns";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestEditHeader } from "components/DesignSystem/RequestEditHeader";
import { RequestStatusTimeline } from "components/DesignSystem/RequestStatusTimeline";
import { RequestCancelButton } from "components/DesignSystem/RequestCancelButton";
import RequestDetailsSection from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import {
  themes,
  infoStyle,
  childBoxStyle,
} from "services/constants";
import { tooltipStyleProps } from "services/constants/Styles";
import { customAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import { atleastOneCharacterWithSpace } from "services/constants/patterns";
import { minimum3Characters } from "services/constants/ErrorMessages";
import { CustomAutoComplete } from "components/CustomComponents";
import { CustomTimePicker } from "components/CustomComponents/CustomTimePicker";
import { WorkAdjustmentAttendanceType } from "pages/TeamZone/TeamZoneHeaders";
import { CustomizedDatePicker } from "components/CustomComponents/CustomizedDatePicker";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useFetch } from "services/hooks/useFetch";
import { appliedOn, formatBreakDuration } from "./attendance/attendanceHelpers";

dayjs.extend(customParseFormat);

export const EditWorkAdjustment = ({
  open = true,
  onClose,
  setUpdateData,
  updateData,
  selectedData,
  reCallApi,
  check_access,
}) => {
  const requestCompanyId = useSelector((state) => state.tokenReducer.topbarCompanyId);
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);
  const [showEditOption, setShowEditOption] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelReasonError, setCancelReasonError] = useState("");
  const {
    setValue,
    handleSubmit,
    register,
    watch,
    control,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      user_name: "",
      attendance_type: "",
      break_category: "OT Break",
      button_loader: false,
      start_date: null,
      startTime: null,
      endTime: null,
      break_to_cancel: [],
      reason_for_work_adjustment: "",
    },
  });
  const { result: initialBreakData } = useFetch(
    `${domain}team_zone/approve/work_adjustment/user-break-context/?user_id=${userDetails?.id}&company_id=${requestCompanyId}`,
  );
  const attendanceDate = watch("attandance_date");
  const normalizedAttendanceDate = dayjs.isDayjs(attendanceDate)
    ? attendanceDate
    : dayjs(
        String(attendanceDate || ""),
        ["DD/MM/YYYY", "YYYY-MM-DD", "MM/DD/YYYY"],
        true,
      );
  const isValidAttendanceDate =
    normalizedAttendanceDate.isValid() &&
    normalizedAttendanceDate.year() >= 1900 &&
    normalizedAttendanceDate.year() <= 9999;
  const shouldFetchBreakData =
    isValidAttendanceDate && watch("attendance_type") !== "Cancel Break";
  const { result: breakData, setLoad: reloadBreakData } = useFetch(
    shouldFetchBreakData
      ? `${domain}team_zone/approve/work_adjustment/user-break-context/?user_id=${userDetails?.id}&date=${dayjs(
          normalizedAttendanceDate,
        ).format("YYYY-MM-DD")}&company_id=${requestCompanyId}`
      : null,
  );
  const shouldFetchCancelBreakOptions =
    isValidAttendanceDate && watch("attendance_type") === "Cancel Break";
  const { result: cancelBreakApiData, setLoad: reloadCancelBreakOptions } =
    useFetch(
      shouldFetchCancelBreakOptions
        ? `${domain}my_zone/apply/work_adjustment/cancel-break-options/?user_id=${userDetails?.id}&date=${dayjs(
            normalizedAttendanceDate,
          ).format("YYYY-MM-DD")}&company_id=${requestCompanyId}`
        : null,
    );
  const breakCategory = watch("break_category");
  const selectedAttendanceType = watch("attendance_type");
  const requestTypeLabel = {
    compoff: "Comp Off",
    "Comp off": "Comp Off",
    "Comp Off": "Comp Off",
    permission: "Permission",
    Permission: "Permission",
    onduty: "On Duty",
    "On Duty": "On Duty",
    Break: "Apply Break",
    "Cancel Break": "Cancel Break",
  }[selectedAttendanceType || selectedData?.attendance_type] || "Work Adjustment";
  const breakStartTime = watch("startTime");
  const breakEndTime = watch("endTime");
  const shiftDetails = breakData?.date_stats?.shift_details;
  const shiftStartTime = breakData?.date_stats?.shift_details?.shift_start_time;
  const shiftEndTime = breakData?.date_stats?.shift_details?.shift_end_time;
  const isOvernightShift = Boolean(shiftDetails?.is_overnight_shift);
  const getTimeInMinutes = (timeValue) =>
    dayjs(timeValue).hour() * 60 + dayjs(timeValue).minute();
  const shiftStartMinutes = shiftStartTime
    ? getTimeInMinutes(dayjs(shiftStartTime, "HH:mm"))
    : null;
  const shiftEndMinutes = shiftEndTime
    ? getTimeInMinutes(dayjs(shiftEndTime, "HH:mm"))
    : null;
  const isTimeWithinShiftWindow = (timeValue, view = "minutes") => {
    if (
      shiftStartMinutes === null ||
      shiftEndMinutes === null ||
      !timeValue ||
      !dayjs(timeValue).isValid()
    ) {
      return false;
    }

    const candidateMinutes = getTimeInMinutes(dayjs(timeValue));

    if (view === "hours") {
      const hourStartMinutes = dayjs(timeValue).hour() * 60;
      const hourEndMinutes = hourStartMinutes + 59;

      return isOvernightShift
        ? hourEndMinutes >= shiftStartMinutes ||
            hourStartMinutes <= shiftEndMinutes
        : hourEndMinutes >= shiftStartMinutes &&
            hourStartMinutes <= shiftEndMinutes;
    }

    return isOvernightShift
      ? candidateMinutes >= shiftStartMinutes ||
          candidateMinutes <= shiftEndMinutes
      : candidateMinutes >= shiftStartMinutes &&
          candidateMinutes <= shiftEndMinutes;
  };
  const shiftMinTime =
    watch("attendance_type") === "Break" &&
    breakCategory === "Shift Break" &&
    shiftStartTime &&
    !isOvernightShift
      ? dayjs(shiftStartTime, "HH:mm")
      : null;
  const shiftMaxTime =
    watch("attendance_type") === "Break" &&
    breakCategory === "Shift Break" &&
    shiftEndTime &&
    !isOvernightShift
      ? dayjs(shiftEndTime, "HH:mm")
      : null;
  const shiftDurationInMinutes =
    shiftStartTime && shiftEndTime
      ? isOvernightShift
        ? 24 * 60 - shiftStartMinutes + shiftEndMinutes
        : shiftEndMinutes - shiftStartMinutes
      : null;
  const otRecords = breakData?.date_stats?.ot_records || [];
  const sortOtRecordsByStartTime = (items = []) =>
    [...items].sort((a, b) => {
      const first = dayjs(a?.ot_start_datetime);
      const second = dayjs(b?.ot_start_datetime);

      if (!first.isValid() || !second.isValid()) {
        return 0;
      }

      return first.valueOf() - second.valueOf();
    });
  const sortedOtRecords = sortOtRecordsByStartTime(otRecords);
  const disableBreakTime = (value, view) => {
    if (
      watch("attendance_type") !== "Break" ||
      !shiftStartTime ||
      !shiftEndTime
    ) {
      return false;
    }

    const isWithinShiftWindow = isTimeWithinShiftWindow(value, view);

    if (breakCategory === "Shift Break") {
      return !isWithinShiftWindow;
    }

    if (breakCategory === "OT Break") {
      if (view === "hours") {
        const hourStartMinutes = dayjs(value).hour() * 60;
        const hourEndMinutes = hourStartMinutes + 59;
        const hasTimeOutsideShift = isOvernightShift
          ? hourStartMinutes < shiftStartMinutes &&
            hourEndMinutes > shiftEndMinutes
          : hourStartMinutes < shiftStartMinutes ||
            hourEndMinutes > shiftEndMinutes;

        return !hasTimeOutsideShift;
      }

      return isWithinShiftWindow;
    }

    return false;
  };
  const getBreakDateTime = (timeValue) => {
    if (!isValidAttendanceDate || !timeValue || !dayjs(timeValue).isValid()) {
      return null;
    }

    const breakDateTime = dayjs(normalizedAttendanceDate)
      .hour(dayjs(timeValue).hour())
      .minute(dayjs(timeValue).minute())
      .second(0);
    const nextDayBreakDateTime = breakDateTime.add(1, "day");

    if (breakCategory === "OT Break" && otRecords.length) {
      const matchingOtRecord = sortedOtRecords.find((record) => {
        const otStart = dayjs(record?.ot_start_datetime);
        const otEnd = dayjs(record?.ot_end_datetime);

        if (!otStart.isValid() || !otEnd.isValid()) {
          return false;
        }

        return (
          ((breakDateTime.isSame(otStart) || breakDateTime.isAfter(otStart)) &&
            (breakDateTime.isSame(otEnd) || breakDateTime.isBefore(otEnd))) ||
          ((nextDayBreakDateTime.isSame(otStart) ||
            nextDayBreakDateTime.isAfter(otStart)) &&
            (nextDayBreakDateTime.isSame(otEnd) ||
              nextDayBreakDateTime.isBefore(otEnd)))
        );
      });

      if (matchingOtRecord) {
        const otStart = dayjs(matchingOtRecord?.ot_start_datetime);
        const otEnd = dayjs(matchingOtRecord?.ot_end_datetime);

        if (
          (nextDayBreakDateTime.isSame(otStart) ||
            nextDayBreakDateTime.isAfter(otStart)) &&
          (nextDayBreakDateTime.isSame(otEnd) ||
            nextDayBreakDateTime.isBefore(otEnd))
        ) {
          return nextDayBreakDateTime;
        }

        return breakDateTime;
      }
    }

    if (
      isOvernightShift &&
      shiftEndMinutes !== null &&
      getTimeInMinutes(dayjs(timeValue)) <= shiftEndMinutes
    ) {
      return breakDateTime.add(1, "day");
    }

    return breakDateTime;
  };
  const selectedBreakStartDateTime = getBreakDateTime(breakStartTime);
  const selectedBreakEndDateTime = getBreakDateTime(breakEndTime);
  const selectedBreakDuration =
    selectedBreakStartDateTime &&
    selectedBreakEndDateTime &&
    selectedBreakEndDateTime.isAfter(selectedBreakStartDateTime)
      ? `${selectedBreakEndDateTime.diff(
          selectedBreakStartDateTime,
          "minute",
        )} minutes`
      : initialBreakData?.manual_break_duration
        ? `${initialBreakData.manual_break_duration} minutes`
        : "-";
  const selectedBreakDurationMinutes =
    selectedBreakStartDateTime &&
    selectedBreakEndDateTime &&
    selectedBreakEndDateTime.isAfter(selectedBreakStartDateTime)
      ? selectedBreakEndDateTime.diff(selectedBreakStartDateTime, "minute")
      : initialBreakData?.manual_break_duration || 0;
  const configuredBreakDurationMinutes =
    breakData?.manual_break_duration ??
    initialBreakData?.manual_break_duration ??
    0;
  const manualShiftBreaks = [
    ...(breakData?.date_stats?.manual_breaks_pending || []),
    ...(breakData?.date_stats?.manual_breaks_approved || []),
  ].filter(
    (item) => String(item?.break_type || "").toLowerCase() === "shift_break",
  );
  const existingShiftBreaks = [
    ...(breakData?.date_stats?.real_punch_breaks || []),
    ...manualShiftBreaks,
  ].filter((item) => item?.id !== selectedData?.id);
  const isAttendanceValid = breakData?.date_stats?.is_attendance_valid;
  const isOtAllowed =
    breakData?.date_stats?.is_ot_allowed ?? breakData?.is_ot_allowed;
  const applyDisabledReason =
    watch("attendance_type") === "Break" && isValidAttendanceDate
      ? isAttendanceValid === false
        ? "No attendance record found on the selected date"
        : breakCategory === "OT Break" && isOtAllowed === false
          ? "Not eligible to apply OT break on the selected date"
          : ""
      : "";
  const manualOtBreaks = [
    ...(breakData?.date_stats?.manual_breaks_pending || []),
    ...(breakData?.date_stats?.manual_breaks_approved || []),
  ].filter(
    (item) => String(item?.break_type || "").toLowerCase() === "ot_break",
  );
  const existingOtBreaks = [
    ...otRecords.flatMap((record) => record?.ot_breaks || []),
    ...manualOtBreaks,
  ]
    .filter((item) => item?.id !== selectedData?.id)
    .filter(
      (item, index, arr) =>
        arr.findIndex(
          (other) =>
            (other?.start_time || other?.break_start_time) ===
              (item?.start_time || item?.break_start_time) &&
            (other?.end_time || other?.break_end_time) ===
              (item?.end_time || item?.break_end_time),
        ) === index,
    );
  const sortBreakItemsByStartTime = (items = []) =>
    [...items].sort((a, b) => {
      const first = dayjs(a?.start_time || a?.break_start_time);
      const second = dayjs(b?.start_time || b?.break_start_time);

      if (!first.isValid() || !second.isValid()) {
        return 0;
      }

      return first.valueOf() - second.valueOf();
    });
  const sortedShiftBreaks = sortBreakItemsByStartTime(existingShiftBreaks);
  const sortedOtBreaks = sortBreakItemsByStartTime(existingOtBreaks);
  const cancelBreakApiList = Array.isArray(cancelBreakApiData)
    ? cancelBreakApiData
    : Array.isArray(cancelBreakApiData?.break_options)
      ? cancelBreakApiData.break_options
      : [];
  const resolveCancelBreakCategory = (item) => {
    const label = item?.category_label || item?.type_label;
    if (label === "Shift Break" || label === "OT Break") {
      return {
        code: label === "Shift Break" ? "Shift_break" : "Ot_break",
        label,
      };
    }

    const code = String(
      item?.category_code || item?.break_type || "",
    ).toLowerCase();
    const isOt = ["ot_break", "ot break"].includes(code) || item?.over_time_id;
    const isShift = !isOt;
    return {
      code: isShift ? "Shift_break" : "Ot_break",
      label: isShift ? "Shift Break" : "OT Break",
    };
  };
  const cancelBreakOptions = cancelBreakApiList.map((item, index) => {
    const category = resolveCancelBreakCategory(item);
    return {
      ...item,
      cancel_row_id: item?.break_id ?? item?.id ?? index,
      category_code: category.code,
      category_label: category.label,
    };
  });
  const previousBreakCategoryRef = useRef();
  const previousAttendanceTypeRef = useRef();
  const isPrefillJustRanRef = useRef(false);
  const renderBreakEntries = (breaks, emptyMessage) => {
    if (!breaks?.length) {
      return (
        <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
          {emptyMessage}
        </Typography>
      );
    }

    return (
      <Box display="flex" flexDirection="column" gap={0.75}>
        <Grid container spacing={0.75} sx={{ px: 0.75 }}>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              Start
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              End
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary">
              Duration
            </Typography>
          </Grid>
        </Grid>
        {breaks.map((item, index) => (
          <Box
            key={
              item?.id ||
              `${item?.start_time || item?.break_start_time || "break"}_${index}`
            }
            sx={{
              p: 0.75,
              borderRadius: 1,
              backgroundColor: "#ffffff",
              border: `1px solid ${themes?.miliedLightGray}`,
            }}
          >
            <Grid container spacing={0.75}>
              <Grid item xs={4}>
                <Typography sx={{ lineHeight: 1.3 }}>
                  {item?.start_time || item?.break_start_time
                    ? dayjs(item?.start_time || item?.break_start_time).format(
                        "HH:mm",
                      )
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography sx={{ lineHeight: 1.3 }}>
                  {item?.end_time || item?.break_end_time
                    ? dayjs(item?.end_time || item?.break_end_time).format(
                        "HH:mm",
                      )
                    : "-"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography sx={{ lineHeight: 1.3 }}>
                  {formatBreakDuration(item?.duration)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        ))}
      </Box>
    );
  };
  const validateOtBreakRange = (currentStart, currentEnd) => {
    if (!currentStart || !currentEnd) {
      return null;
    }

    const hasOtBreakOverlap = existingOtBreaks.some((item) => {
      const existingStart = dayjs(item?.start_time);
      const existingEnd = dayjs(item?.end_time);

      if (!existingStart.isValid() || !existingEnd.isValid()) {
        return false;
      }

      return (
        currentStart.isBefore(existingEnd) && currentEnd.isAfter(existingStart)
      );
    });

    if (hasOtBreakOverlap) {
      return "Selected OT break overlaps with an existing OT break";
    }

    return null;
  };
  useEffect(() => {
    const updateSelectedData = () => {
      let attendance_Type =
        selectedData?.attendance_type === "compoff"
          ? "Comp off"
          : selectedData?.attendance_type;
      let type =
        attendance_Type === "permission"
          ? "permission"
          : attendance_Type === "onduty"
            ? "On Duty"
            : attendance_Type;
      setValue("user_name", userDetails?.username);
      setValue("attendance_type", type);
      setValue(
        "break_category",
        selectedData?.break_type === "Shift_break" ? "Shift Break" : "OT Break",
      );
      setValue(
        "attandance_date",
        selectedData?.date
          ? dayjs(selectedData?.date.split("-").reverse().join("-"))
          : null,
      );
      setValue(
        "earned_date",
        selectedData?.earned_date
          ? dayjs(selectedData?.earned_date.split("-").reverse().join("-"))
          : null,
      );
      setValue(
        "comp_off_request_date",
        selectedData?.requested_date
          ? dayjs(selectedData?.requested_date.split("-").reverse().join("-"))
          : null,
      );
      setValue(
        "startTime",
        selectedData?.break_start_time
          ? dayjs(selectedData?.break_start_time)
          : dayjs(selectedData?.start_time, "HH:mm"),
      );
      setValue(
        "endTime",
        selectedData?.break_end_time
          ? dayjs(selectedData?.break_end_time)
          : dayjs(selectedData?.end_time, "HH:mm"),
      );
      setValue(
        "break_to_cancel",
        Array.isArray(selectedData?.breaks_to_cancel)
          ? selectedData.breaks_to_cancel.map((entry) => ({
              id: entry?.break_id,
              type: entry?.break_type,
            }))
          : selectedData?.break_id
            ? [{ id: selectedData.break_id, type: selectedData.break_type }]
            : [],
      );
      setValue("reason_for_work_adjustment", selectedData?.reason);
      isPrefillJustRanRef.current = true;
      setUpdateData(false);
    };
    if (updateData) {
      updateSelectedData();
    }
  }, [
    selectedData?.attendance_type,
    selectedData?.date,
    selectedData?.earned_date,
    selectedData?.end_time,
    selectedData?.break_end_time,
    selectedData?.break_start_time,
    selectedData?.break_id,
    selectedData?.break_type,
    selectedData?.breaks_to_cancel,
    selectedData?.reason,
    selectedData?.requested_date,
    selectedData?.start_time,
    setUpdateData,
    setValue,
    updateData,
    userDetails?.username,
  ]);

  useEffect(() => {
    if (isValidAttendanceDate && selectedAttendanceType === "Cancel Break") {
      reloadBreakData(true);
      reloadCancelBreakOptions(true);
    }
  }, [
    attendanceDate,
    isValidAttendanceDate,
    selectedAttendanceType,
    reloadBreakData,
    reloadCancelBreakOptions,
  ]);

  useEffect(() => {
    if (isPrefillJustRanRef.current) {
      isPrefillJustRanRef.current = false;
      return;
    }

    const previousBreakCategory = previousBreakCategoryRef.current;
    const previousAttendanceType = previousAttendanceTypeRef.current;
    const hasSwitchedToOtBreak =
      previousBreakCategory !== undefined &&
      previousBreakCategory !== "OT Break" &&
      breakCategory === "OT Break";
    const hasSwitchedToShiftBreak =
      previousBreakCategory !== undefined &&
      previousBreakCategory !== "Shift Break" &&
      breakCategory === "Shift Break";
    const hasEnteredBreakWithOtCategory =
      previousAttendanceType !== undefined &&
      previousAttendanceType !== "Break" &&
      selectedAttendanceType === "Break" &&
      breakCategory === "OT Break";

    previousBreakCategoryRef.current = breakCategory;
    previousAttendanceTypeRef.current = selectedAttendanceType;

    if (hasSwitchedToOtBreak || hasSwitchedToShiftBreak) {
      setValue("startTime", null);
      setValue("endTime", null);
      clearErrors(["startTime", "endTime"]);
    }

    if (
      selectedAttendanceType !== "Break" ||
      breakCategory !== "OT Break" ||
      !sortedOtRecords.length
    ) {
      return;
    }

    if (
      !hasSwitchedToOtBreak &&
      !hasEnteredBreakWithOtCategory &&
      breakStartTime &&
      breakEndTime
    ) {
      return;
    }

    const defaultOtRecord = sortedOtRecords[sortedOtRecords.length - 1];
    const defaultStartTime = dayjs(defaultOtRecord?.ot_start_datetime);

    if (!defaultStartTime.isValid()) {
      return;
    }

    setValue("startTime", defaultStartTime);
    setValue(
      "endTime",
      defaultStartTime.add(configuredBreakDurationMinutes, "minute"),
    );
  }, [
    selectedAttendanceType,
    breakCategory,
    breakStartTime,
    breakEndTime,
    configuredBreakDurationMinutes,
    clearErrors,
    setValue,
    sortedOtRecords,
  ]);

  useEffect(() => {
    if (selectedAttendanceType !== "Cancel Break") {
      setValue("break_to_cancel", []);
    }
  }, [selectedAttendanceType, setValue]);

  const onSubmit = async (data) => {
    setValue("button_loader", true);
    let payload = {
      id: selectedData?.id,
      user_id: userDetails?.id,
      attendance_type:
        data?.attendance_type === "Break"
          ? "break"
          : data?.attendance_type === "Cancel Break"
            ? "cancel_break"
            : data?.attendance_type,
      reason: data?.reason_for_work_adjustment,
    };
    if (data?.attendance_type === "Cancel Break") {
      const selectedBreakId = (data?.break_to_cancel || [])[0]?.id;
      payload = {
        attendance_type: "Cancel Break",
        id: selectedData?.id,
        break_id: selectedBreakId,
        reason: data?.reason_for_work_adjustment,
      };
    }
    if (data?.attendance_type === "Break") {
      payload.break_type =
        data?.break_category === "OT Break" ? "Ot_break" : "Shift_break";
      const calculatedBreakStartDateTime = getBreakDateTime(watch("startTime"));
      const calculatedBreakEndDateTime = getBreakDateTime(watch("endTime"));
      payload.break_start_time = calculatedBreakStartDateTime?.format(
        "YYYY-MM-DDTHH:mm:ssZ",
      );
      payload.break_end_time = calculatedBreakEndDateTime?.format(
        "YYYY-MM-DDTHH:mm:ssZ",
      );
      payload.duration = selectedBreakDurationMinutes;
      payload.date = normalizedAttendanceDate.format("YYYY-MM-DD");
    }
    if (
      data?.attendance_type === "On Duty" ||
      data?.attendance_type === "Permission"
    ) {
      payload.date = normalizedAttendanceDate.format("YYYY-MM-DD");
      payload.start_time = dayjs(watch("startTime")).format("HH:mm");
      payload.end_time = dayjs(watch("endTime")).format("HH:mm");
    }
    if (data?.attendance_type === "Comp off") {
      payload.earned_date = data?.earned_date?.format("YYYY-MM-DD");
      payload.requested_date =
        data?.comp_off_request_date?.format("YYYY-MM-DD");
    }
    const response = await customAPI(
      `${domain}my_zone/apply/work_adjustment/pre-special-attendance?company_id=${requestCompanyId}`,
      "PUT",
      payload,
    );
    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      reCallApi(true);
      onClose();
      reset();
      setValue("button_loader", false);
    } else {
      setValue("button_loader", false);
      showToast("error", response?.message);
    }
  };

  const closeCancelDialog = () => {
    setShowCancelDialog(false);
    setCancelReason("");
    setCancelReasonError("");
  };

  const handleCancelRequest = async () => {
    const trimmedReason = cancelReason.trim();
    if (!trimmedReason) {
      setCancelReasonError("Reason for cancellation is required");
      return;
    }
    if (trimmedReason.length < 3) {
      setCancelReasonError(minimum3Characters);
      return;
    }

    setCancelling(true);
    const response = await customAPI(
      `${domain}my_zone/apply/work_adjustment/pre-special-attendance/?company_id=${requestCompanyId}`,
      "PUT",
      {
        id: selectedData?.id,
        attendance_type: selectedData?.attendance_type,
        status: "Cancelled",
        reason: selectedData?.reason,
        cancelled_reason: trimmedReason,
        ...(selectedData?.attendance_type === "Cancel Break" && { cancel: true }),
      },
    );
    setCancelling(false);
    closeCancelDialog();
    if (response.ok) {
      showToast("success", response?.responseDetails?.message || "Request cancelled");
      reCallApi(true);
      onClose();
    } else {
      showToast("error", response?.message);
    }
  };

  const attendanceType = (event, data) => {
    if (data) {
      setValue("attendance_type", data?.label);
    }
  };

  const handleReason = (event) => {
    if (event.target.value.length <= 1024) {
      setValue("reason_for_work_adjustment", event.target.value, {
        shouldValidate: true,
      });
    }
  };

  const BreakContextInfo = () => {
    if (watch("attendance_type") !== "Break") {
      return null;
    }

    if (breakCategory === "Shift Break") {
      return (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: 1.5,
            border: `1px solid ${themes?.miliedLightGray}`,
            backgroundColor: "#f8fafc",
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: "1rem", mb: 1.25 }}>
            Shift Details
          </Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={4}>
              <Typography
                sx={{ fontSize: "0.9rem", color: "text.secondary", mb: 0.5 }}
              >
                Shift Name
              </Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 400 }}>
                {shiftDetails?.shift_name || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                sx={{ fontSize: "0.9rem", color: "text.secondary", mb: 0.5 }}
              >
                Shift Start
              </Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 400 }}>
                {shiftDetails?.shift_start_time || "-"}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography
                sx={{ fontSize: "0.9rem", color: "text.secondary", mb: 0.5 }}
              >
                Shift End
              </Typography>
              <Typography sx={{ fontSize: "1rem", fontWeight: 400 }}>
                {shiftDetails?.shift_end_time || "-"}
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontWeight: 600, mb: 0.75 }}>
              Existing Shift Breaks
            </Typography>
            {renderBreakEntries(sortedShiftBreaks, "No shift breaks found")}
          </Box>
        </Paper>
      );
    }

    if (breakCategory === "OT Break") {
      if (!otRecords?.length) {
        return null;
      }

      return (
        <Paper
          elevation={0}
          sx={{
            mt: 1,
            p: 1.5,
            border: `1px solid ${themes?.miliedLightGray}`,
            backgroundColor: "#fffaf0",
          }}
        >
          <Typography sx={{ fontWeight: 700, mb: 1 }}>OT Details</Typography>
          <Box display="flex" flexDirection="column" gap={1}>
            <Grid container spacing={0.75} sx={{ px: 0.75 }}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  OT Start
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  OT End
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Break Taken
                </Typography>
              </Grid>
            </Grid>
            {sortedOtRecords.map((record, index) => (
              <Box
                key={record?.ot_id || index}
                sx={{
                  p: 0.75,
                  borderRadius: 1,
                  backgroundColor: "#ffffff",
                  border: `1px solid ${themes?.miliedLightGray}`,
                }}
              >
                <Grid container spacing={0.75}>
                  <Grid item xs={4}>
                    <Typography sx={{ lineHeight: 1.3 }}>
                      {record?.ot_start_datetime
                        ? dayjs(record.ot_start_datetime).format("HH:mm")
                        : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography sx={{ lineHeight: 1.3 }}>
                      {record?.ot_end_datetime
                        ? dayjs(record.ot_end_datetime).format("HH:mm")
                        : "-"}
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography sx={{ lineHeight: 1.3 }}>
                      {record?.total_ot_break_taken || "00:00"}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontWeight: 600, mb: 0.75 }}>
              Existing OT Breaks
            </Typography>
            {renderBreakEntries(sortedOtBreaks, "No OT breaks found")}
          </Box>
        </Paper>
      );
    }

    return null;
  };

  const isBreakAttendance =
    String(
      watch("attendance_type") || selectedData?.attendance_type || "",
    ).toLowerCase() === "break";
  const isCancelBreakAttendance =
    String(
      watch("attendance_type") || selectedData?.attendance_type || "",
    ).toLowerCase() === "cancel break";
  const hasBreakTimeRange = Boolean(
    selectedData?.break_start_time || selectedData?.break_end_time,
  );
  const breakStartDisplay = selectedData?.break_start_time
    ? dayjs(selectedData.break_start_time).format("HH:mm")
    : selectedData?.start_time || "-";
  const breakEndDisplay = selectedData?.break_end_time
    ? dayjs(selectedData.break_end_time).format("HH:mm")
    : selectedData?.end_time || "-";
  const breakDurationDisplay = selectedData?.duration
    ? formatBreakDuration(selectedData.duration)
    : selectedData?.break_start_time && selectedData?.break_end_time
      ? formatBreakDuration(
          dayjs(selectedData.break_end_time).diff(
            dayjs(selectedData.break_start_time),
            "minute",
          ),
        )
      : selectedData?.duration || "-";
  const cancelBreakRows = Array.isArray(selectedData?.breaks)
    ? selectedData.breaks.map((item, index) => ({
        id: item?.break_id ?? item?.id ?? index,
        startTime: item?.break_start_time || item?.start_time,
        endTime: item?.break_end_time || item?.end_time,
        duration: formatBreakDuration(item?.duration),
        type: item?.category_label || item?.break_type || item?.type || "-",
      }))
    : [];
  const formatBreakTableTime = (timeValue) => {
    if (!timeValue) {
      return "-";
    }

    const parsedTime = dayjs(timeValue);

    return parsedTime.isValid() ? parsedTime.format("HH:mm") : timeValue;
  };

  const LegacyViewAdjustmentDetails = () => {
    return (
      <React.Fragment>
        <Paper
          elevation={3}
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: "#f3eaf2", // light lavender background
          }}
        >
          {/* Header Row */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                backgroundColor: "#f8bbd0",
                borderRadius: 1,
                p: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EventNote sx={{ color: "#c2185b" }} />
            </Box>

            <Typography variant="subtitle1" fontWeight={500}>
              Attendance Type
            </Typography>
          </Box>

          {/* Value */}
          <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>
            {selectedData?.attendance_type}
          </Typography>
        </Paper>
        <Paper
          elevation={3}
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: "#e3e7ee",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTime color="primary" />
            <Typography fontWeight={500}>
              {watch("attendance_type") === "Comp off"
                ? "Comp off Earned On"
                : "Date"}
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ mt: 2 }} fontWeight={600}>
            {watch("attendance_type") === "Comp off"
              ? selectedData?.earned_date
                ? selectedData?.earned_date
                : null
              : selectedData?.date
                ? selectedData?.date
                : null}
          </Typography>
        </Paper>
        {watch("attendance_type") !== "Comp off" ? (
          isCancelBreakAttendance ? (
            <Paper
              elevation={3}
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 2,
                backgroundColor: "#e3e7ee",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <AccessTime color="primary" />
                <Typography fontWeight={500}>Break Details</Typography>
              </Box>
              <Mui.TableContainer>
                <Mui.Table size="small">
                  <Mui.TableHead>
                    <Mui.TableRow>
                      <Mui.TableCell>Start Time</Mui.TableCell>
                      <Mui.TableCell>End Time</Mui.TableCell>
                      <Mui.TableCell>Duration</Mui.TableCell>
                    </Mui.TableRow>
                  </Mui.TableHead>
                  <Mui.TableBody>
                    {cancelBreakRows.length ? (
                      cancelBreakRows.map((item) => (
                        <Mui.TableRow key={item.id}>
                          <Mui.TableCell>
                            {formatBreakTableTime(item.startTime)}
                          </Mui.TableCell>
                          <Mui.TableCell>
                            {formatBreakTableTime(item.endTime)}
                          </Mui.TableCell>
                          <Mui.TableCell>{formatBreakDuration(item.duration)}</Mui.TableCell>
                        </Mui.TableRow>
                      ))
                    ) : (
                      <Mui.TableRow>
                        <Mui.TableCell colSpan={3} align="center">
                          No break details available
                        </Mui.TableCell>
                      </Mui.TableRow>
                    )}
                  </Mui.TableBody>
                </Mui.Table>
              </Mui.TableContainer>
            </Paper>
          ) : (
          <Mui.Grid container spacing={3} sx={{ mt: 1 }}>
            <Mui.Grid item xs={isBreakAttendance ? 4 : 6}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: "#e3e7ee",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AccessTime color="primary" />
                  <Typography fontWeight={500}>Start Time</Typography>
                </Box>
                <Typography variant="h6" sx={{ mt: 2 }} fontWeight={600}>
                  {isBreakAttendance || hasBreakTimeRange
                    ? breakStartDisplay
                    : selectedData?.start_time}
                </Typography>
              </Paper>
            </Mui.Grid>

            <Mui.Grid item xs={isBreakAttendance ? 4 : 6}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: "#e3e7ee",
                }}
              >
                {" "}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AccessTime color="primary" />
                  <Typography fontWeight={500}>End Time</Typography>
                </Box>
                <Typography variant="h6" sx={{ mt: 2 }} fontWeight={600}>
                  {isBreakAttendance || hasBreakTimeRange
                    ? breakEndDisplay
                    : selectedData?.end_time}
                </Typography>
              </Paper>
            </Mui.Grid>
            {isBreakAttendance ? (
              <Mui.Grid item xs={4}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#e3e7ee",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <AccessTime color="primary" />
                    <Typography fontWeight={500}>Duration</Typography>
                  </Box>
                  <Typography variant="h6" sx={{ mt: 2 }} fontWeight={600}>
                    {breakDurationDisplay}
                  </Typography>
                </Paper>
              </Mui.Grid>
            ) : null}
          </Mui.Grid>
          )
        ) : (
          <Paper
            elevation={3}
            sx={{
              mt: 3,
              p: 2,
              borderRadius: 2,
              backgroundColor: "#e3e7ee",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccessTime color="primary" />
              <Typography fontWeight={500}>
                {watch("attendance_type") === "Comp off"
                  ? "Comp off Requested For"
                  : "Date"}
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ mt: 2 }} fontWeight={600}>
              {watch("attendance_type") === "Comp off"
                ? selectedData?.requested_date
                  ? selectedData?.requested_date
                  : null
                : selectedData?.date
                  ? selectedData?.date
                  : null}
            </Typography>
          </Paper>
        )}

        {/* Reason */}
        <Paper
          elevation={3}
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: "#f1ece4",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <HelpOutline sx={{ color: "#f57c00" }} />
            <Typography fontWeight={500}>Reason</Typography>
          </Box>
          <Typography sx={{ mt: 2 }} fontWeight={500}>
            {selectedData?.reason}
          </Typography>
        </Paper>
      </React.Fragment>
    );
  };

  const ViewAdjustmentDetails = () => {
    const attendanceType = selectedData?.attendance_type || watch("attendance_type");
    const isCompOff = attendanceType === "Comp off";
    const isBreak = attendanceType === "Break";
    const isCancelBreak = attendanceType === "Cancel Break";
    const displayAttendanceType =
      attendanceType === "Break" ? "Apply Break" : attendanceType;
    const requestStatus = String(selectedData?.status || "");
    const rejectedReason =
      requestStatus === "Rejected" ? selectedData?.rejected_reason || "" : "";
    const cancelledReason =
      selectedData?.cancelled_reason ||
      (requestStatus === "Cancelled" ? selectedData?.rejected_reason || "" : "");
    const dateLabel = isCompOff ? "Comp off Earned On" : "Date";
    const dateValue = isCompOff ? selectedData?.earned_date : selectedData?.date;
    const fields = [
      { icon: EventNote, label: "Attendance Type", value: displayAttendanceType || "-", accent: detailAccentPalette.pink },
      { icon: AccessTime, label: dateLabel, value: dateValue || "-", accent: detailAccentPalette.indigo },
      { icon: AccessTime, label: "Applied On", value: appliedOn(selectedData) || "-", accent: detailAccentPalette.blue },
    ];
    if (isBreak) {
      fields.push(
        { icon: AccessTime, label: "Start Time", value: breakStartDisplay || selectedData?.start_time || "-", accent: detailAccentPalette.blue },
        { icon: AccessTime, label: "End Time", value: breakEndDisplay || selectedData?.end_time || "-", accent: detailAccentPalette.green },
        { icon: AccessTime, label: "Duration", value: breakDurationDisplay || "-", accent: detailAccentPalette.purple },
      );
    }
    if (isCancelBreak) {
      fields.push({
        icon: AccessTime,
        label: "Cancel Details",
        value: cancelBreakRows.length
          ? cancelBreakRows
              .map(
                (item, index) =>
                  `${index + 1}. ${formatBreakTableTime(item.startTime)} - ${formatBreakTableTime(item.endTime)} (${formatBreakDuration(item.duration)})${item.type && item.type !== "-" ? `, ${item.type}` : ""}`,
              )
              .join("; ")
          : "-",
        accent: detailAccentPalette.purple,
        multiline: true,
      });
    }
    fields.push({
      icon: HelpOutline,
      label: "Reason",
      value: selectedData?.reason || "-",
      accent: detailAccentPalette.orange,
      multiline: true,
    });
    if (rejectedReason) {
      fields.push({
        icon: HelpOutline,
        label: "Reject Reason",
        value: rejectedReason,
        accent: detailAccentPalette.purple,
        multiline: true,
      });
    }
    if (cancelledReason) {
      fields.push({
        icon: HelpOutline,
        label: "Cancel Reason",
        value: cancelledReason,
        accent: detailAccentPalette.purple,
        multiline: true,
      });
    }
    return <RequestDetailsSection fields={fields} />;
  };

  const requestStatus = String(selectedData?.status || "");
  const statusFlowData = {
    ...(selectedData || {}),
    applied_on:
      selectedData?.applied_on ||
      selectedData?.applied_date ||
      selectedData?.created_at ||
      selectedData?.created_date,
    cancelled_by: selectedData?.cancelled_by || selectedData?.modified_by,
    cancelled_at:
      selectedData?.cancelled_at ||
      selectedData?.cancelled_on ||
      (requestStatus === "Cancelled" ? selectedData?.modified_at : undefined),
    cancelled_reason:
      selectedData?.cancelled_reason ||
      (requestStatus === "Cancelled" ? selectedData?.rejected_reason : undefined),
    // selectedData?.approver_name is the assigned approver, stamped at apply
    // time before anyone has acted -- it is not proof of approval. Falling
    // back to it here resurrected the pre-stamped name as "Approved by" for
    // a request that was cancelled straight from Pending and never
    // actually approved.
    approved_by: selectedData?.approved_by,
    approved_at: selectedData?.approved_at || selectedData?.approved_on,
    rejected_reason:
      requestStatus === "Rejected" ? selectedData?.rejected_reason : undefined,
  };

  // Edit/Cancel used to always render for a Pending request and only block
  // on click (toast) when the viewer lacked edit access -- a view-only user
  // saw both actions and hit "Access denied" after opening them. Both now
  // require check_access (the same "My Request" editor permission the New
  // Request button on WorkAdjustment/index.js is gated on) before they
  // render at all, matching how TeamZone Approve's own detail panels hide
  // rather than disable an action the viewer isn't allowed to take.
  const editAction =
    check_access && selectedData?.status === "Pending" ? (
      <Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
        <IconButton onClick={() => setShowEditOption(true)}>
          <Edit />
        </IconButton>
      </Tooltip>
    ) : null;

  const canCancel =
    check_access && !showEditOption && selectedData?.status === "Pending";

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={showEditOption ? undefined : `${requestTypeLabel} Request`}
      subtitle={
        showEditOption
          ? undefined
          : `Create a ${requestTypeLabel} request for a selected period`
      }
      header={
        showEditOption ? (
          <RequestEditHeader title={`${requestTypeLabel} Request`} />
        ) : undefined
      }
      headerActions={showEditOption ? null : editAction}
      footer={
        showEditOption ? (
          <Mui.Stack
            direction={{ xs: "column-reverse", sm: "row" }}
            spacing={1.5}
            justifyContent="flex-end"
            sx={{ width: "100%" }}
          >
            <SecondaryButton
              onClick={() => setShowEditOption(false)}
              disabled={Boolean(watch("button_loader"))}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              Discard
            </SecondaryButton>
            <Tooltip
              title={applyDisabledReason}
              arrow
              disableHoverListener={!applyDisabledReason}
              slotProps={tooltipStyleProps}
            >
              <Box
                component="span"
                sx={{ display: "inline-flex", width: { xs: "100%", sm: "auto" } }}
              >
                <PrimaryButton
                  type="submit"
                  onClick={handleSubmit(onSubmit)}
                  loading={Boolean(watch("button_loader"))}
                  disabled={
                    Boolean(watch("button_loader")) ||
                    Boolean(applyDisabledReason)
                  }
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                >
                  Update
                </PrimaryButton>
              </Box>
            </Tooltip>
          </Mui.Stack>
        ) : canCancel ? (
          <RequestCancelButton onClick={() => setShowCancelDialog(true)} />
        ) : null
      }
    >
      <GlobalConfirmDialog
        open={showCancelDialog}
        onCancel={closeCancelDialog}
        onConfirm={handleCancelRequest}
        title="Cancel Request"
        confirmLabel="Cancel Request"
        cancelLabel="Keep"
        loading={cancelling}
        loadingLabel="Cancelling..."
        danger
        reasonValue={cancelReason}
        onReasonChange={(value) => {
          setCancelReason(value);
          if (cancelReasonError) setCancelReasonError("");
        }}
        reasonError={cancelReasonError}
        reasonPlaceholder="Provide a reason for cancellation"
        reasonMinRows={2}
        maxWidth="sm"
      >
        <Mui.Typography sx={{ fontSize: 14, lineHeight: 1.5, mb: 2 }}>
          Enter the reason for cancelling this request.
        </Mui.Typography>
      </GlobalConfirmDialog>
      <Box sx={{ overflow: "auto", p: 2 }}>
        <Box sx={childBoxStyle}>
          <RequestDetailHeader
            userDetails={{
              image_url: userDetails?.image_url,
              employee_id: userDetails?.employee_id
                ? userDetails?.employee_id
                : "-",
              user_name: userDetails?.first_name,
              department_name: userDetails?.department_name
                ? userDetails?.department_name
                : "-",
              designation: selectedData?.designation
                ? selectedData?.designation
                : "-",
            }}
            status={selectedData?.status}
            variant="employeeDetailsCard"
          />
          {showEditOption ? null : (
            <>
              <Box sx={{ mt: 2 }}>
                <ViewAdjustmentDetails />
              </Box>
              <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
                <RequestStatusTimeline endDate={[selectedData?.date, selectedData?.requested_date]} data={statusFlowData} hidePendingStep />
              </Box>
            </>
          )}
        </Box>
      </Box>
      {showEditOption ? (
        <Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ overflow: "auto", p: 2 }}>
              <Box sx={childBoxStyle}>
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>
                    Work Adjustment Type
                  </Typography>
                  <Typography
                    {...infoStyle}
                    sx={{
                      ...infoStyle.sx,
                      mb: 0,
                    }}
                  >
                    Choose the type of Work Adjustment
                  </Typography>
                  <CustomAutoComplete
                    dataList={WorkAdjustmentAttendanceType}
                    rules={{
                      ...register("attendance_type", {
                        required: "Attendance Type is required",
                      }),
                    }}
                    selectedValue={watch("attendance_type")}
                    updateDataFunction={attendanceType}
                    placeHolder="Select adjustment type"
                    errorMessage={
                      errors?.attendance_type?.message
                        ? errors?.attendance_type?.message
                        : ""
                    }
                    textBoxError={errors?.attendance_type ? true : false}
                  />
                </Box>
                {watch("attendance_type") === "Comp off" ? null : (
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>Date</Typography>
                    <Typography
                      {...infoStyle}
                      sx={{
                        ...infoStyle.sx,
                        mb: 0,
                      }}
                    >
                      Select the applicable attendance date
                    </Typography>
                    <CustomizedDatePicker
                      name="attandance_date"
                      control={control}
                      required
                      requiredMessage="Date is required"
                      placeholder="Select start date" //minDate={}
                      disableFuture
                      handleDisabledFutureDate={true}
                    />
                  </Box>
                )}
                {watch("attendance_type") === "Break" && (
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      Break Category
                    </Typography>
                    <Typography {...infoStyle}>
                      Select when the overtime will be considered.
                    </Typography>
                    <Box>
                      <Controller
                        name="break_category"
                        control={control}
                        defaultValue="OT Break"
                        render={({ field }) => (
                          <FormControl component="fieldset">
                            <RadioGroup row sx={{ gap: 2 }} {...field}>
                              <FormControlLabel
                                value="OT Break"
                                control={<Radio />}
                                label="OT Break"
                              />
                              <FormControlLabel
                                value="Shift Break"
                                control={<Radio />}
                                label="Shift Break"
                              />
                            </RadioGroup>
                          </FormControl>
                        )}
                      />
                    </Box>
                    <BreakContextInfo />
                  </Box>
                )}
                {watch("attendance_type") === "Cancel Break" && (
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>
                      Break to Cancel
                    </Typography>
                    <Typography {...infoStyle}>
                      Select one or more existing break entries to cancel
                    </Typography>
                    <input
                      type="hidden"
                      {...register("break_to_cancel", {
                        validate: (value) =>
                          watch("attendance_type") !== "Cancel Break" ||
                          (Array.isArray(value) && value.length > 0) ||
                          "Select at least one break to cancel",
                      })}
                    />
                    <Paper
                      elevation={0}
                      sx={{
                        mt: 1,
                        border: `1px solid ${themes?.miliedLightGray}`,
                        borderRadius: 1,
                        overflow: "hidden",
                      }}
                    >
                      <Grid
                        container
                        sx={{
                          px: 1.5,
                          py: 1,
                          backgroundColor: "#f8fafc",
                          display: { xs: "none", sm: "flex" },
                        }}
                      >
                        <Grid item xs={1} />
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary">
                            Break Start
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary">
                            Break End
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary">
                            Duration
                          </Typography>
                        </Grid>
                        <Grid item xs={2}>
                          <Typography variant="caption" color="text.secondary">
                            Type
                          </Typography>
                        </Grid>
                      </Grid>
                      {cancelBreakOptions.length ? (
                        cancelBreakOptions.map((item) => {
                          const selectedBreaksToCancel =
                            watch("break_to_cancel") || [];
                          const isChecked = selectedBreaksToCancel.some(
                            (entry) => entry?.id === item?.cancel_row_id,
                          );
                          return (
                            <Grid
                              container
                              key={item?.cancel_row_id}
                              sx={{
                                px: 1.5,
                                py: 1,
                                alignItems: "center",
                                borderTop: `1px solid ${themes?.miliedLightGray}`,
                              }}
                            >
                              <Grid item xs={12} sm={1}>
                                <Checkbox
                                  size="small"
                                  checked={isChecked}
                                  onChange={() => {
                                    const next = isChecked
                                      ? selectedBreaksToCancel.filter(
                                          (entry) =>
                                            entry?.id !== item?.cancel_row_id,
                                        )
                                      : [
                                          ...selectedBreaksToCancel,
                                          {
                                            id: item?.cancel_row_id,
                                            type: item?.category_code,
                                          },
                                        ];
                                    setValue("break_to_cancel", next);
                                    clearErrors("break_to_cancel");
                                  }}
                                />
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: { xs: "block", sm: "none" } }}
                                >
                                  Break Start
                                </Typography>
                                <Typography sx={{ lineHeight: 1.3 }}>
                                  {item?.start_time || item?.break_start_time
                                    ? dayjs(
                                        item?.start_time ||
                                          item?.break_start_time,
                                      ).format("HH:mm")
                                    : "-"}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: { xs: "block", sm: "none" } }}
                                >
                                  Break End
                                </Typography>
                                <Typography sx={{ lineHeight: 1.3 }}>
                                  {item?.end_time || item?.break_end_time
                                    ? dayjs(
                                        item?.end_time || item?.break_end_time,
                                      ).format("HH:mm")
                                    : "-"}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={3}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: { xs: "block", sm: "none" } }}
                                >
                                  Duration
                                </Typography>
                                <Typography sx={{ lineHeight: 1.3 }}>
                                  {formatBreakDuration(item?.duration)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6} sm={2}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: { xs: "block", sm: "none" } }}
                                >
                                  Type
                                </Typography>
                                <Typography sx={{ lineHeight: 1.3 }}>
                                  {item?.category_label}
                                </Typography>
                              </Grid>
                            </Grid>
                          );
                        })
                      ) : (
                        <Typography
                          sx={{
                            p: 1.5,
                            fontSize: "0.9rem",
                            color: "text.secondary",
                          }}
                        >
                          No breaks found for the selected date
                        </Typography>
                      )}
                    </Paper>
                    {errors?.break_to_cancel?.message ? (
                      <Typography
                        sx={{
                          color: "error.main",
                          fontSize: "0.75rem",
                          mt: 0.5,
                        }}
                      >
                        {errors.break_to_cancel.message}
                      </Typography>
                    ) : null}
                  </Box>
                )}
                {watch("attendance_type") === "Comp off" ? (
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      Comp off Earned On
                    </Typography>
                    <Typography
                      {...infoStyle}
                      sx={{
                        ...infoStyle.sx,
                        mb: 0,
                      }}
                    >
                      Select the date employee worked on as weekoff/holiday
                    </Typography>
                    <CustomizedDatePicker
                      name="earned_date"
                      control={control}
                      required
                      requiredMessage="Comp off earned date is required"
                      placeholder="Select Comp off earned date" //minDate={}
                      onChange={(newValue) => {
                        setValue("earned_date", newValue);
                      }}
                      disableFuture
                      handleDisabledFutureDate={true}
                    />
                  </Box>
                ) : null}
                {watch("attendance_type") === "Comp off" ? (
                  <Box>
                    <Typography sx={{ fontWeight: 600 }}>
                      Comp off Requested For
                    </Typography>
                    <Typography
                      {...infoStyle}
                      sx={{
                        ...infoStyle.sx,
                        mb: 0,
                      }}
                    >
                      Select the dare employee wants to avail compensatory off
                    </Typography>
                    <CustomizedDatePicker
                      name="comp_off_request_date"
                      control={control}
                      required={
                        watch("attendance_type") === "Comp off" ? true : false
                      }
                      requiredMessage="Comp off requested date is required"
                      placeholder="Select Comp Off requested date" //minDate={}
                    />
                  </Box>
                ) : null}
                {watch("attendance_type") === "Comp off" ||
                watch("attendance_type") === "Cancel Break" ? null : (
                  <Box
                    display="flex"
                    gap={2}
                    sx={{ flexDirection: { xs: "column", sm: "row" } }}
                  >
                    {/* Start Time */}
                    <Box flex={1}>
                      <Typography sx={{ fontWeight: 600 }}>
                        Start Time
                      </Typography>
                      <Typography {...infoStyle}>
                        Select the start time
                      </Typography>
                      <CustomTimePicker
                        name="startTime"
                        control={control}
                        errors={errors}
                        minTime={shiftMinTime}
                        maxTime={shiftMaxTime}
                        shouldDisableTime={disableBreakTime}
                        validateFn={(value) => {
                          if (!value || !dayjs(value).isValid()) {
                            return "Start time is required";
                          }
                          if (
                            watch("attendance_type") === "Break" &&
                            breakCategory === "Shift Break" &&
                            !isTimeWithinShiftWindow(value)
                          ) {
                            return "Start time must fall within shift hours";
                          }
                          if (
                            watch("attendance_type") === "Break" &&
                            breakCategory === "Shift Break" &&
                            breakEndTime &&
                            dayjs(breakEndTime).isValid()
                          ) {
                            const currentStart = getBreakDateTime(value);
                            const currentEnd = getBreakDateTime(breakEndTime);

                            if (!currentStart || !currentEnd) {
                              return true;
                            }

                            const hasOverlap = existingShiftBreaks.some(
                              (item) => {
                                const existingStart = dayjs(
                                  item?.start_time || item?.break_start_time,
                                );
                                const existingEnd = dayjs(
                                  item?.end_time || item?.break_end_time,
                                );

                                if (
                                  !existingStart.isValid() ||
                                  !existingEnd.isValid()
                                ) {
                                  return false;
                                }

                                return (
                                  currentStart.isBefore(existingEnd) &&
                                  currentEnd.isAfter(existingStart)
                                );
                              },
                            );

                            if (hasOverlap) {
                              return "Selected break time overlaps with an existing break";
                            }

                            if (
                              shiftDurationInMinutes !== null &&
                              currentEnd.diff(currentStart, "minute") >
                                shiftDurationInMinutes
                            ) {
                              return "Selected break duration cannot exceed shift hours";
                            }
                          }
                          if (
                            watch("attendance_type") === "Break" &&
                            breakCategory === "OT Break" &&
                            breakEndTime &&
                            dayjs(breakEndTime).isValid()
                          ) {
                            const currentStart = getBreakDateTime(value);
                            const currentEnd = getBreakDateTime(breakEndTime);
                            const otBreakValidation = validateOtBreakRange(
                              currentStart,
                              currentEnd,
                            );

                            if (otBreakValidation) {
                              return otBreakValidation;
                            }
                          }
                          return true;
                        }}
                      />
                    </Box>

                    {/* End Time */}
                    <Box flex={1}>
                      <Typography sx={{ fontWeight: 600 }}>End Time</Typography>
                      <Typography {...infoStyle}>
                        Select the end time
                      </Typography>
                      <CustomTimePicker
                        name="endTime"
                        control={control}
                        errors={errors}
                        minTime={shiftMinTime}
                        maxTime={shiftMaxTime}
                        shouldDisableTime={disableBreakTime}
                        validateFn={(value) => {
                          const startTime = watch("startTime");

                          if (!value || !dayjs(value).isValid()) {
                            return "End time is required";
                          }

                          if (startTime) {
                            if (watch("attendance_type") === "Break") {
                              const currentStart = getBreakDateTime(startTime);
                              const currentEnd = getBreakDateTime(value);

                              if (
                                currentStart &&
                                currentEnd &&
                                currentEnd.isBefore(currentStart)
                              ) {
                                return "End time must be after start time";
                              }
                            } else if (
                              dayjs(value).isBefore(dayjs(startTime))
                            ) {
                              return "End time must be after start time";
                            }
                          }
                          if (
                            watch("attendance_type") === "Break" &&
                            breakCategory === "Shift Break" &&
                            !isTimeWithinShiftWindow(value)
                          ) {
                            return "End time must fall within shift hours";
                          }
                          if (
                            watch("attendance_type") === "Break" &&
                            breakCategory === "Shift Break" &&
                            startTime &&
                            dayjs(startTime).isValid()
                          ) {
                            const currentStart = getBreakDateTime(startTime);
                            const currentEnd = getBreakDateTime(value);

                            if (!currentStart || !currentEnd) {
                              return true;
                            }

                            const hasOverlap = existingShiftBreaks.some(
                              (item) => {
                                const existingStart = dayjs(
                                  item?.start_time || item?.break_start_time,
                                );
                                const existingEnd = dayjs(
                                  item?.end_time || item?.break_end_time,
                                );

                                if (
                                  !existingStart.isValid() ||
                                  !existingEnd.isValid()
                                ) {
                                  return false;
                                }

                                return (
                                  currentStart.isBefore(existingEnd) &&
                                  currentEnd.isAfter(existingStart)
                                );
                              },
                            );

                            if (hasOverlap) {
                              return "Selected break time overlaps with an existing break";
                            }

                            if (
                              shiftDurationInMinutes !== null &&
                              currentEnd.diff(currentStart, "minute") >
                                shiftDurationInMinutes
                            ) {
                              return "Selected break duration cannot exceed shift hours";
                            }
                          }
                          if (
                            watch("attendance_type") === "Break" &&
                            breakCategory === "OT Break" &&
                            startTime &&
                            dayjs(startTime).isValid()
                          ) {
                            const currentStart = getBreakDateTime(startTime);
                            const currentEnd = getBreakDateTime(value);
                            const otBreakValidation = validateOtBreakRange(
                              currentStart,
                              currentEnd,
                            );

                            if (otBreakValidation) {
                              return otBreakValidation;
                            }
                          }

                          return true;
                        }}
                      />
                    </Box>
                    {watch("attendance_type") === "Break" && (
                      <Box flex={1}>
                        <Typography sx={{ fontWeight: 600 }}>
                          Duration
                        </Typography>
                        <Typography {...infoStyle}>
                          Configured break duration
                        </Typography>
                        <Box
                          sx={{
                            minHeight: 40,
                            border: `1px solid ${themes?.miliedLightGray}`,
                            borderRadius: 1,
                            px: 2,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <Typography>{formatBreakDuration(selectedBreakDuration)}</Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>Reason</Typography>
                  <Typography {...infoStyle}>
                    Enter the reason for this attendance request
                  </Typography>
                  <CustomTextBox
                    type="multiline"
                    rules={{
                      ...register("reason_for_work_adjustment", {
                        required: "Reason is required",
                        validate: {
                          minLengthNoSpaces: (value) => {
                            if (value?.trim().length < 3 && value !== "") {
                              return minimum3Characters;
                            } else if (
                              value?.trim().length > 255 &&
                              value !== ""
                            ) {
                              return "Maximum length is 255 characters";
                            } else {
                              return true;
                            }
                          },
                          atleastOneCharacterWithSpaceReg: (value) => {
                            if (
                              atleastOneCharacterWithSpace.test(value) ||
                              value === ""
                            ) {
                              return true;
                            } else {
                              return "Atleast one alphabet required";
                            }
                          },
                        },
                      }),
                    }}
                    selectedValue={watch("reason_for_work_adjustment")}
                    updateDataFunction={handleReason}
                    placeHolder="Provide a reason for work adjustment"
                    errorMessage={
                      errors?.reason_for_work_adjustment?.message
                        ? errors?.reason_for_work_adjustment?.message
                        : ""
                    }
                    textBoxError={
                      errors?.reason_for_work_adjustment ? true : false
                    }
                  />
                </Box>
              </Box>
            </Box>
          </form>
        </Box>
      ) : null}
    </GlobalSidePanel>
  );
};
